<template>
    <div class="vb">
        <div class="vb-left">
            <div class="vb-picker">
                <label class="vb-label">Version</label>
                <v-select
                    :modelValue="selectedId"
                    :options="versionOptions"
                    label="label"
                    :reduce="option => option.id"
                    :clearable="false"
                    @update:modelValue="emit('select', $event)"
                />
            </div>
            <StatusChip v-if="selected" :status="selected.status" :tones="budgetVersionTones"/>
            <span v-if="selected" class="vb-amount">Total {{ commaFormat(selected.total_amount) }}</span>
            <span v-if="hint" class="vb-hint">{{ hint }}</span>
        </div>

        <div class="vb-actions">
            <button v-if="canSubmit" type="button" class="btn vb-btn vb-btn-primary" :disabled="busy" @click="emit('action', 'submit')">
                <i class="fa-regular fa-paper-plane"></i><span>Submit for Approval</span>
            </button>
            <button v-if="canReopen" type="button" class="btn vb-btn vb-btn-primary" :disabled="busy" @click="emit('action', 'reopen')">
                <i class="fa-solid fa-rotate-left"></i><span>Reopen as Draft</span>
            </button>
            <button v-if="canDiscard" type="button" class="btn vb-btn vb-btn-ghost" :disabled="busy" @click="emit('action', 'discard')">
                <i class="fa-regular fa-trash-can"></i><span>Discard Revision</span>
            </button>
            <button v-if="canRevise" type="button" class="btn vb-btn vb-btn-primary" :disabled="busy" @click="emit('revise')">
                <i class="fa-solid fa-code-branch"></i><span>Create Revised Budget</span>
            </button>
            <button type="button" class="btn vb-btn vb-btn-ghost" @click="emit('history')">
                <i class="fa-solid fa-clock-rotate-left"></i><span>History</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import StatusChip from '@/components/atom/StatusChip.vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { budgetVersionTones, toCapitalizeFirst } from '@/services/utils/global.js'

const props = defineProps({
    versions: {
        type: Array,
        default: () => []
    },
    selectedId: {
        type: Number,
        default: null
    },
    workingId: {
        type: Number,
        default: null
    },
    busy: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['select', 'action', 'revise', 'history'])

const { commaFormat } = figureFormatter()

const versionOptions = computed(() => props.versions.map(version => ({
    id: version.id,
    label: `V${version.version_no} - ${toCapitalizeFirst(version.status)}`
})))

const selected = computed(() => props.versions.find(version => version.id === props.selectedId))

const working = computed(() => props.versions.find(version => version.id === props.workingId))

const isWorkingSelected = computed(() => !! selected.value && props.selectedId === props.workingId)

const workingStatus = computed(() => {
    if (! working.value) return ''
    return working.value.status
})

const canSubmit = computed(() => isWorkingSelected.value && workingStatus.value === 'draft')

const canReopen = computed(() => isWorkingSelected.value && workingStatus.value === 'rejected')

const canDiscard = computed(() => isWorkingSelected.value && ! working.value.is_current && ['draft', 'rejected'].includes(workingStatus.value))

const canRevise = computed(() => isWorkingSelected.value && workingStatus.value === 'active')

const hints = {
    draft: 'Draft - change the lines, then submit for approval.',
    pending: 'Waiting for approval on the Budget Allocation Approval page - lines are locked.',
    rejected: 'Rejected - reopen it to make changes.',
    active: 'Approved budget in use - create a revised budget to change it.'
}

const hint = computed(() => {
    if (! selected.value) return ''
    if (! isWorkingSelected.value) return 'Old version - view and export only.'
    return hints[workingStatus.value] || ''
})
</script>

<style scoped>
.vb {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 14px;
    border: 1px solid #eaecf0;
    border-radius: 10px;
    background: #f9fafb;
}
.vb-left {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.vb-picker {
    width: 220px;
}
.vb-label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #344054;
}
.vb-picker :deep(.vs__dropdown-toggle) {
    min-height: 38px;
    border-radius: 8px;
    background: #fff;
}
.vb-amount {
    font-size: 14px;
    font-weight: 600;
    color: #101828;
    line-height: 26px;
    white-space: nowrap;
}
.vb-hint {
    font-size: 13px;
    color: #475467;
    line-height: 26px;
}
.vb-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.vb-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}
.vb-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.vb-btn-primary {
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
}
.vb-btn-primary:hover:not(:disabled) {
    background: #5e50ee;
    color: #fff;
}
.vb-btn-ghost {
    background: #fff;
    border: 1px solid #e4e7ec;
    color: #475467;
}
.vb-btn-ghost:hover:not(:disabled) {
    background: #f2f4f7;
    color: #101828;
}
@media (max-width: 576px) {
    .vb-picker {
        width: 100%;
    }
}
</style>
